import { HttpStatusCode } from "../../../enums/http-status-code.enum";
import { HealthCheckStatus } from "../enums/health-check-status.enum";
import { HealthCheck, HealthCheckEntryView, HealthCheckView } from "../models/health-check.model";
import { IdentityProviderAvailabilityResult } from "../models/identity-provider-probe.model";

export const IDENTITY_PROVIDER_CHECK_NAME = 'identity_provider_openid';

/**
 * Some services (e.g. Account) register health checks with ASP.NET Core's default response
 * writer instead of the JSON report writer the other tiled services use, so /hc returns only
 * the bare status text - "Healthy", "Unhealthy", "Degraded" - with no entries breakdown, even
 * though the response is (misleadingly) sent with a Content-Type of application/json. Parses
 * either shape into a HealthCheck; a bare/unparseable body becomes a HealthCheck with an empty
 * entries map rather than throwing.
 */
export function parseHealthCheckResponse(raw: string): HealthCheck {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    parsed = raw.trim();
  }

  if (parsed && typeof parsed === 'object' && 'status' in (parsed as object)) {
    return parsed as HealthCheck;
  }

  const status = (typeof parsed === 'string' ? parsed : raw).trim() as HealthCheckStatus;
  return { status, totalDuration: '', entries: {} };
}

export function mapToHealthDetailsView(data: HealthCheck): HealthCheckView {
  const result: HealthCheckView = {};
  result.code = data.status === HealthCheckStatus.Healthy ? HttpStatusCode.OK : HttpStatusCode.SERVICE_UNAVAILABLE_ERROR;
  result.checks = [];

  const entries = data.entries;
  for (const property in entries) {
    if (entries.hasOwnProperty(property)) {
      const entry = entries[property];
      const view: HealthCheckEntryView = {
        name: property,
        message: entry.description || 'OK',
        elapsed: entry.duration,
        exception: entry.exception,
        status: entry.status,
        quota: entry.data['quota']
      };

      if (property === IDENTITY_PROVIDER_CHECK_NAME && entry.data?.['availability']) {
        view.identityProvider = entry.data['availability'] as IdentityProviderAvailabilityResult;
      }

      result.checks.push(view);
    }
  }

  return result;
}
